import { type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPortalRoot } from './portalRoot';

interface DrawerProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: ReactNode;
    footer?: ReactNode;
    width?: 'sm' | 'md' | 'lg' | 'xl';
    side?: 'left' | 'right';
    closeOnEsc?: boolean;
    closeOnOverlayClick?: boolean;
    className?: string;
}

const Drawer = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    width = 'md',
    side = 'right',
    closeOnEsc = true,
    closeOnOverlayClick = true,
    className = ''
}: DrawerProps) => {
    // Shared container with Modal, resolved once: a container that changes between renders remounts
    // the drawer (see portalRoot).
    const [portalRoot] = useState(getPortalRoot);

    // Lock background scroll while the drawer is open.
    useEffect(() => {
        if (!isOpen) return;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    // ESC to close.
    useEffect(() => {
        if (!isOpen || !closeOnEsc) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, closeOnEsc, onClose]);

    const widthClass = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-2xl'
    }[width];

    const closedTransform = side === 'right' ? 'translate-x-full' : '-translate-x-full';
    const sideClass = side === 'right' ? 'right-0 border-l' : 'left-0 border-r';

    const drawerContent = (
        <div className="fixed inset-0 z-50" style={{ pointerEvents: isOpen ? 'auto' : 'none' }}>
            {/* Overlay */}
            <div
                aria-hidden="true"
                className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
                role="presentation"
                onClick={closeOnOverlayClick ? onClose : undefined}
            />

            {/* Panel */}
            <div
                aria-hidden={!isOpen}
                aria-labelledby={title ? 'drawer-title' : undefined}
                aria-modal="true"
                className={`absolute top-0 ${sideClass} flex h-full w-full ${widthClass} flex-col border-gray-800 bg-[#121212] shadow-xl transition-transform duration-200 ease-out ${
                    isOpen ? 'translate-x-0' : closedTransform
                } ${className}`}
                role="dialog"
            >
                {title && (
                    <div className="flex shrink-0 items-center justify-between border-b border-gray-800 px-5 py-4">
                        <h3 className="text-lg font-medium text-white" id="drawer-title">
                            {title}
                        </h3>
                        <button
                            aria-label="Close"
                            className="rounded-md p-1 text-gray-400 hover:bg-[#181818] hover:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                            type="button"
                            onClick={onClose}
                        >
                            <span className="sr-only">Close</span>
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                            </svg>
                        </button>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

                {footer && <div className="flex shrink-0 justify-end gap-2 border-t border-gray-800 px-5 py-4">{footer}</div>}
            </div>
        </div>
    );

    return createPortal(drawerContent, portalRoot);
};

export default Drawer;
